import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from "../../components/Common/Breadcrumb";
import AlbumCard from "../../components/Gallery/AlbumCard";
import AddAlbumModal from "../../components/Gallery/AddAlbumModal";
import EditAlbumModal from "../../components/Gallery/EditAlbumModal";

import { fetchAllAlbums, addAlbum, updateAlbum } from "../../services/galleryService";
import { encrypt } from "../../utils/encryption";

const GalleryListPage = () => {
  const [albums, setAlbums] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: "Gallery" },
    { label: "Albums" },
  ];

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      const data = await fetchAllAlbums();
      setAlbums(data);
    } catch (err) {
      message.error("Failed to load albums");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAlbum = async (newAlbum) => {
    try {
      await addAlbum(newAlbum);
      await fetchAlbums();
      setShowAddModal(false);
      message.success("Album added successfully.");
    } catch (err) {
      message.error(err.message || "Failed to add album.");
    }
  };

  const handleEditAlbum = async (updatedAlbum) => {
    try {
      // Ensure we're sending the correct album_id
      const albumToUpdate = {
        ...updatedAlbum,
        album_id: currentAlbum.album_id // Use the current album's ID
      };
      
      await updateAlbum(albumToUpdate);
      await fetchAlbums();
      setShowEditModal(false);
      setCurrentAlbum(null);
      message.success("Album updated successfully.");
    } catch (err) {
      message.error(err.message || "Failed to update album.");
    }
  };

  const handleEditClick = (album) => {
    setCurrentAlbum(album);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setCurrentAlbum(null);
  };

  const filteredAlbums = albums.filter((album) =>
    album.album_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <Helmet>
        <title>Gallery Albums | CRM JNEC</title>
      </Helmet>

      <Header />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content">
          <Breadcrumb items={breadcrumbItems} />

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title">Gallery Albums</h4>
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control form-control-sm me-3"
                  placeholder="Search by album name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                  Add new album
                </button>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center p-3">Loading albums...</div>
              ) : (
                <>
                  <p className="mb-3">
                    Showing albums {filteredAlbums.length} of {albums.length}
                  </p>
                  <div className="row">
                    {filteredAlbums.length > 0 ? (
                      filteredAlbums.map((album) => (
                        <AlbumCard
                          key={album.album_id}
                          album={{
                            id: album.album_id,
                            title: album.album_name,
                            year: album.album_year,
                            icon: album.album_icon,
                            color: album.album_color,
                            borderColor: album.album_borderColor,
                            textColor: album.album_textColor,
                            imageCount: album.image_count,
                          }}
                          onClick={() =>
                            navigate(`/gallery/${encodeURIComponent(encrypt(album.album_id))}`)
                          }
                          onEdit={() => handleEditClick(album)}
                        />
                      ))
                    ) : (
                      <div className="col-12 text-center py-4">
                        <p>{searchTerm ? "No albums found matching your search." : "No albums found."}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddAlbumModal
        isVisible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddAlbum}
      />

      <EditAlbumModal
        isVisible={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleEditAlbum}
        albumData={currentAlbum}
      />
    </div>
  );
};

export default GalleryListPage;